import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'accent' | 'indigo';
type Size = 'sm' | 'md' | 'icon';

const variants: Record<Variant, string> = {
  primary:     'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm hover:shadow',
  indigo:      'bg-indigo text-white hover:bg-indigo-hover shadow-sm hover:shadow',
  secondary:   'border border-hairline bg-surface text-ink hover:bg-surface-3 shadow-sm hover:shadow',
  ghost:       'hover:bg-muted text-dim hover:text-ink',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
  accent:      'bg-accent text-accent-foreground hover:bg-[oklch(0.60_0.14_75)] shadow-sm',
};

const sizes: Record<Size, string> = {
  sm:   'px-2.5 py-1 text-xs rounded-[var(--radius-control)]',
  md:   'px-3.5 py-1.5 text-sm rounded-[var(--radius-control)]',
  icon: 'h-[34px] w-[34px] p-0 rounded-lg border border-hairline bg-surface text-dim hover:bg-surface-3',
};

const base = [
  'inline-flex items-center justify-center gap-1.5 font-medium',
  'transition-all duration-150 ease-out',
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
  'active:scale-[0.97] active:transition-transform active:duration-100',
  'disabled:opacity-50 disabled:pointer-events-none',
].join(' ');

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', ...props }, ref) => (
    <button
      ref={ref}
      className={cn(base, size === 'icon' ? sizes.icon : cn(variants[variant], sizes[size]), className)}
      {...props}
    />
  )
);
Button.displayName = 'Button';
